// RecipePlugin's shop windows: names, cluster names, ?tab= aliases, and the
// templates each experience load copies.
import { shopWindowTypes } from "@/modules/Constants";
import { icons } from "@/modules/icons";
import ShopWindowConfig, { openingSlideType } from "@/plugin/ShopWindowConfig";

export const shopWindowNames = {
  COOKWARE: "cookware",
  WINE: "wine",
  BOOK: "book",
  METHOD: "method",
  PRODUCT: "product",
  LIST: "list",
  ASK: "ask",
  TEST: "test",
};

export const clusterNames = {
  COOKWARE: "cookware",
  WINE: "wine",
  BOOK: "book",
  METHOD: "method",
  PRODUCT: "product",
  INGREDIENT: "ingredient",
  // Not a real CDP cluster name — Ask has no CDP-backed cluster (see the
  // RecipePlugin._registerShopWindows bypass). Kept distinct from every real
  // cluster name so it can never collide with the `used` de-dupe map.
  ASK: "ask",
  // Not a CDP cluster either: the Test tab is gated by the demo.test_tab flag.
  TEST: "test",
};

// Play-page deep-link ?tab=<token> friendly aliases → internal shop-window names.
// Tokens not listed here fall through to the shop-window name verbatim, so exact
// names (method, product, cookware, wine, book, list) also work.
export const tabAliases: Record<string, string> = {
  products: shopWindowNames.PRODUCT,
  product: shopWindowNames.PRODUCT,
  equipment: shopWindowNames.COOKWARE,
  cookware: shopWindowNames.COOKWARE,
  ingredients: shopWindowNames.LIST,
  essentials: shopWindowNames.PRODUCT, // the PRODUCT window is labelled "Essentials"
  method: shopWindowNames.METHOD,
  wine: shopWindowNames.WINE,
  book: shopWindowNames.BOOK,
  ask: shopWindowNames.ASK,
};

export const SHOP_WINDOW_TEMPLATES = [
  new ShopWindowConfig(
    shopWindowNames.METHOD,
    shopWindowTypes.METHOD,
    "Method",
    clusterNames.METHOD,
    icons.CHECKLIST,
    false,
    {
      openingSlideType: openingSlideType.CUSTOM,
      showHintTap: true,
      showPagination: true,
      scrub: true,
    },
  ),
  new ShopWindowConfig(
    shopWindowNames.LIST,
    shopWindowTypes.LIST,
    "Ingredients",
    clusterNames.INGREDIENT,
    icons.CART,
    true,
    {},
  ),
  // No CDP cluster: shown only while the demo.test_tab flag is on (see the
  // isTest bypass in RecipePlugin._registerShopWindows).
  new ShopWindowConfig(
    shopWindowNames.TEST,
    shopWindowTypes.TEST,
    "Test",
    clusterNames.TEST,
    icons.CHECKLIST,
    true,
    {},
  ),
  new ShopWindowConfig(
    shopWindowNames.PRODUCT,
    shopWindowTypes.PRODUCT,
    "Essentials",
    clusterNames.PRODUCT,
    icons.FOOD,
    false,
    {
      isFavorites: false,
      openingSlideType: openingSlideType.CUSTOM,
      showHintTap: true,
      showPagination: true,
      scrub: true,
    },
  ),
  new ShopWindowConfig(
    shopWindowNames.BOOK,
    shopWindowTypes.PRODUCT,
    "Books",
    clusterNames.BOOK,
    icons.BOOK,
    false,
    {
      isFavorites: false,
      openingSlideType: openingSlideType.NONE,
      showHintTap: true,
      showPagination: true,
    },
  ),
  new ShopWindowConfig(
    shopWindowNames.COOKWARE,
    shopWindowTypes.PRODUCT,
    "Equipment",
    clusterNames.COOKWARE,
    icons.COOKWARE,
    false,
    {
      isFavorites: false,
      openingSlideType: openingSlideType.NONE,
      showHintTap: true,
      showPagination: true,
    },
  ),
  new ShopWindowConfig(
    shopWindowNames.WINE,
    shopWindowTypes.PRODUCT,
    "Drinks",
    clusterNames.WINE,
    icons.WINE,
    false,
    {
      isFavorites: false,
      openingSlideType: openingSlideType.NONE,
      showHintTap: true,
      showPagination: true,
    },
  ),
  // No CDP cluster — see the isAsk bypass in RecipePlugin._registerShopWindows.
  // isPortraitOverlay: true — in portrait/mobile the chat pops over the
  // whole screen like Ingredients (not the inline panel), so the keyboard +
  // transcript get the full height. "Pixie" (the assistant's brand name)
  // with a distinct AI icon so it reads as an AI conversation, not a
  // leave-a-message form, and stands apart from the recipe tabs.
  new ShopWindowConfig(
    shopWindowNames.ASK,
    shopWindowTypes.ASK,
    "Pixie",
    clusterNames.ASK,
    icons.CHAT_AI,
    true,
    {
      showHintTap: false,
    },
  ),
];
